import getIn from 'transmute/getIn';
import { useSelector, useDispatch } from 'react-redux';
import UIFloatingForm from 'UIComponents/form/UIFloatingForm';
import UIFloatingFormControl from 'UIComponents/form/UIFloatingFormControl';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import UICurrencyInput from 'UIComponents/input/UICurrencyInput';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useAccessLevel } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { HIDDEN, READ_ONLY } from 'customer-data-objects/property/AccessLevel';
import PropertyInputHidden from './PropertyInputHidden';
import { DealAmountCellDecorator } from './DealAmountCellDecorator';
import { getDealSplitsEnabled } from '../../selectors/portalSettings';
import { getCompanyCurrency } from '../../selectors/multiCurrencySelectors';
import { getProperties } from '../../selectors/properties';
import { getStagedDealsUpdates } from '../../selectors/stagedDealsUpdatesSelectors';
import { updateDealProperty } from '../../actions/StagedDealsUpdatesActions';
import { getDealSplitAmountForDealRow, getDealSplitPercentageForDealRow, hasDealSplit } from '../../utils/deals';
import { COLUMN_NAMES } from '../../constants/Table';
import { DEAL_PROPERTIES } from '../../constants/CrmSearchConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CURRENCY_PRECISION = '2';
const DealAmountCell = ({
  row,
  columnName,
  requiredCellProps
}) => {
  const dispatch = useDispatch();
  const propertyDefinition = useSelector(getProperties)[columnName];
  const dealSplitsEnabled = useSelector(getDealSplitsEnabled);
  const homeCurrencyCode = useSelector(getCompanyCurrency);
  const canEditObject = Boolean(getIn(['currentUserPermissions', 'canEdit'], row));

  // Since this is a handrolled cell renderer, we must manually handle access ourselves
  const accessLevel = useAccessLevel({
    objectType: 'DEAL',
    propertyName: columnName
  });
  const isHiddenAccess = accessLevel === HIDDEN;
  const stagedDealUpdates = useSelector(getStagedDealsUpdates)[row.id];
  const stagedValue = stagedDealUpdates && stagedDealUpdates[columnName];
  const doesPropertyHaveStagedValue = stagedDealUpdates && columnName in stagedDealUpdates;
  let amount = row.properties[columnName] ? Number(row.properties[columnName].value) : undefined;
  const totalAmount = amount;
  if (amount) {
    amount = getDealSplitAmountForDealRow(row, dealSplitsEnabled, amount);
  }
  const value = doesPropertyHaveStagedValue ? stagedValue : amount;
  const userReadOnlyAccessOrIsntEditable = hasDealSplit(row, !!dealSplitsEnabled) || accessLevel === READ_ONLY || columnName === COLUMN_NAMES.WEIGHTING_AMOUNT || !canEditObject;
  let currencyCode = '';
  if (columnName === DEAL_PROPERTIES.AMOUNT_IN_HOME_CURRENCY || columnName === DEAL_PROPERTIES.WEIGHTED_AMOUNT_IN_HOME_CURRENCY) {
    currencyCode = homeCurrencyCode;
  } else {
    currencyCode = row.properties['deal_currency_code'] && row.properties['deal_currency_code'].value ? row.properties['deal_currency_code'].value : homeCurrencyCode;
  }
  const dealSplitPercentage = getDealSplitPercentageForDealRow(row, dealSplitsEnabled);
  return /*#__PURE__*/_jsx("td", Object.assign({}, requiredCellProps, {
    children: isHiddenAccess ? /*#__PURE__*/_jsx(PropertyInputHidden, {
      property: propertyDefinition
    }) : /*#__PURE__*/_jsxs(UIFloatingForm, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "start",
        flexDirection: "row",
        justifyContent: "end",
        children: [/*#__PURE__*/_jsx(UIFloatingFormControl, {
          className: "p-all-0",
          hideEditIcon: true,
          children: /*#__PURE__*/_jsx(UICurrencyInput, {
            className: "p-all-0 text-right",
            currency: currencyCode,
            isNativeCurrency: false,
            min: 0,
            onChange: e => {
              dispatch(updateDealProperty({
                dealId: row.id,
                property: columnName,
                value: e.target.value
              }));
            },
            placeholder: I18n.formatIntlCurrency(0, {
              currencyDisplay: 'code',
              precision: Number(CURRENCY_PRECISION),
              currencyCode
            }),
            readOnly: userReadOnlyAccessOrIsntEditable,
            styled: true,
            value: value
          })
        }), /*#__PURE__*/_jsx(DealAmountCellDecorator, {
          deal: row
        })]
      }), dealSplitPercentage && !!totalAmount && /*#__PURE__*/_jsx(Flex, {
        alignItems: "start",
        "data-test-id": "deal-split-help-text",
        flexDirection: "row",
        justifyContent: "end",
        children: /*#__PURE__*/_jsx(Text, {
          color: "text-core-subtle",
          marginBlockEnd: "100",
          typography: "detail-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "dealTable.dealAmountCell.dealSplitText",
            options: {
              percentage: I18n.formatPercentage(dealSplitPercentage * 100, {
                precision: 2
              }),
              totalDealAmount: I18n.formatIntlCurrency(totalAmount, {
                currencyCode
              })
            }
          })
        })
      })]
    })
  }));
};
export default DealAmountCell;